import * as React from "react";
import {
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    ColumnDef,
    ExpandedState,
    getExpandedRowModel,
} from "@tanstack/react-table";
import { Input } from "components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "components/ui/table";
import TablePagination from "./table-pagination";
import { useEffect, useState } from "react";
import { getOrderData, markOrderAsDone, Order } from "action/order-action";
import { toast } from "sonner";
import Loading from "components/loading";
import { Checkbox } from "components/ui/checkbox";
import { Button } from "components/ui/button";
import SiteBreadcrumb from "components/site-breadcrumb";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "components/ui/dialog";
import { CURRENCY } from "helpers/constants";
// Import CSSTransition and TransitionGroup from react-transition-group
import { CSSTransition, TransitionGroup } from "react-transition-group";
import "./StorboltOrderListingComponent.css";
import { useSessionControl } from "hooks/use-session-control"; // Import your CSS for animations

const StorboltOrderListingComponent = () => {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        []
    );
    const [columnVisibility, setColumnVisibility] =
        React.useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = React.useState({});
    const [expanded, setExpanded] = React.useState<ExpandedState>({});
    const [data, setData] = React.useState<Order[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const {sessionControl} = useSessionControl();

    useEffect(() => {
        setIsLoading(true);
        getOrderData()
            .then((response) => {
                if (response.status === 200) {
                    setData(response.body);
                    if (response.message) {
                        toast.error(response.message);
                    }
                } else {
                    sessionControl(response.status);
                    toast.error(response.error);
                }
                setIsLoading(false);
            })
            .catch((err: any) => {
                toast.error("Failed to get orders, an error occurred");
                setIsLoading(false);
            });
    }, []);

    const handleMarkAsDone = (order: Order) => {
        setSelectedOrder(order);
        setShowDialog(true);
    };

    const confirmMarkAsDone = () => {
        if (selectedOrder) {
            const toastId = toast.loading("Marking order as done...");
            // Remove the order from data
            setData((prevData) =>
                prevData.filter((order) => order.id !== selectedOrder.id)
            );
            setShowDialog(false);
            setSelectedOrder(null);

            markOrderAsDone(selectedOrder.id)
                .then((response) => {
                    if (response.status === 200) {
                        toast.success("Order marked as done successfully", {
                            id: toastId,
                        });
                    } else {
                        sessionControl(response.status);
                        toast.error(response.error, {id: toastId});
                    }
                })
                .catch((err: any) => {
                    console.error(err);
                    toast.error("Failed to mark order as done, an error occurred", {
                        id: toastId,
                    });
                });
        }
    };

    const cancelMarkAsDone = () => {
        setShowDialog(false);
        setSelectedOrder(null);
    };

    const columns: ColumnDef<Order>[] = [
        {
            id: "select",
            header: ({table}) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected()}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                />
            ),
            cell: ({row}) => (
                <div className="xl:w-16">
                    <Checkbox
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => {
                            row.toggleSelected(!!value);
                            // Removed row expansion from checkbox
                        }}
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                        }}
                        aria-label="Select row"
                    />
                </div>
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "recipient_name",
            header: "Customer Name",
            cell: ({row}) => <span>{row.getValue("recipient_name")}</span>,
        },
        {
            accessorKey: "recipient_phone",
            header: "Customer Phone",
            cell: ({row}) => <span>{row.getValue("recipient_phone")}</span>,
        },
        {
            accessorKey: "created_at",
            header: "Order Date",
            cell: ({row}) => {
                const dateObj = new Date(row.getValue("created_at"));

                const options: Intl.DateTimeFormatOptions = {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                };

                const formattedDate = new Intl.DateTimeFormat("en-US", options).format(
                    dateObj
                );

                return <span>{formattedDate}</span>;
            },
        },
        {
            accessorKey: "product_cost",
            header: "Paid",
            cell: ({row}) => {
                return <span>{CURRENCY}{row.getValue("product_cost")}</span>;
            },
        },
        {
            accessorKey: "is_done",
            header: "Status",
            cell: ({row}) => {
                return <span>{row.getValue("is_done") ? "Done" : "Not Done"}</span>;
            },
        },
        {
            id: "markAsDone",
            header: "Mark as Done",
            cell: ({row}) => {
                return (
                    <Button
                        variant="shadow"
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                            handleMarkAsDone(row.original);
                        }}
                    >
                        Mark as Done
                    </Button>
                );
            },
        },
    ];

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        onExpandedChange: setExpanded,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
            expanded,
        },
    });

    const toggleRowExpansion = (rowId: string) => {
        table.setExpanded((old) => ({
            ...(old as { [key: string]: boolean }),
            [rowId]: !old[rowId],
        }));
    };

    return (
        <div className="w-full h-screen mb-10">
            <SiteBreadcrumb/>
            <div className="flex items-center flex-wrap gap-2 py-4 px-5">
                <div className="flex-1 text-xl font-medium text-default-900 whitespace-nowrap">
                    Pending Orders
                </div>
                <div className="flex-none">
                    <Input
                        placeholder="Search..."
                        value={
                            (table.getColumn("recipient_name")?.getFilterValue() as string) ??
                            ""
                        }
                        onChange={(event) =>
                            table
                                .getColumn("recipient_name")
                                ?.setFilterValue(event.target.value)
                        }
                        className="max-w-sm "
                    />
                </div>
            </div>

            <Table>
                <TableHeader className="bg-default-200">
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <React.Fragment key={row.id}>
                                <TableRow
                                    data-state={row.getIsSelected() && "selected"}
                                    onClick={() => toggleRowExpansion(row.id)}
                                    className="cursor-pointer"
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} className="h-[75px]">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                {/*
                  Wrap the expanded content in TransitionGroup and CSSTransition
                */}
                                <TransitionGroup component={null}>
                                    {row.getIsExpanded() ? (
                                        <CSSTransition
                                            key={row.id}
                                            timeout={300}
                                            classNames="expand"
                                        >
                                            <TableRow>
                                                <TableCell colSpan={columns.length}>
                                                    {/* Expanded content */}
                                                    <div className="p-4">
                                                        <strong>Order Details:</strong>
                                                        {row.original.items && row.original.items.length > 0 ? (
                                                            <ul className="list-disc pl-5">
                                                                {row.original.items.map((item, index) => (
                                                                    <li
                                                                        className="py-4 list-none"
                                                                        key={`${item.product_id}-${index}`}
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            <img
                                                                                src={item.product_details.image_url}
                                                                                alt={item.product_details.name}
                                                                                className="w-12 h-12 object-cover"
                                                                            />
                                                                            <div>
                                                                                <p className="font-medium">
                                                                                    {item.product_details.name}
                                                                                </p>
                                                                                <p>
                                                                                    Quantity: {item.quantity}
                                                                                    <br/>
                                                                                    Price: {CURRENCY}
                                                                                    {item.price}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p>No items in this order.</p>
                                                        )}
                                                        <p className="mt-4">
                                                            <strong>Delivery Address:</strong>{" "}
                                                            {row.original.delivery_address}
                                                        </p>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        </CSSTransition>
                                    ) : null}
                                </TransitionGroup>
                            </React.Fragment>
                        ))
                    ) : isLoading ? (
                        <TableRow>
                            <TableCell className="h-[75px]" colSpan={columns.length}>
                                <Loading/>
                            </TableCell>
                        </TableRow>
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center">
                                No Pending Orders.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            <TablePagination table={table}/>

            {/* Dialog for Mark as Done */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Mark Order as Done</DialogTitle>
                    </DialogHeader>
                    <p>Are you sure you want to mark this order as done?</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={cancelMarkAsDone}>
                            Cancel
                        </Button>
                        <Button variant="default" onClick={confirmMarkAsDone}>
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default StorboltOrderListingComponent;
